import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  IconIncognitoOutline18,
  IconShieldKeyholeOutline18,
  IconUndoOutline18,
  IconUsersOutline18,
} from 'nucleo-ui-outline-18'
import { toast } from 'sonner'
import { ActivitySessionFeed } from '~/components/admin/activity-session-feed'
import { AdminAvatar } from '~/components/admin/admin-avatar'
import { DetailBadge } from '~/components/admin/detail-bits'
import { LoginHistoryList } from '~/components/admin/login-history-list'
import { UserOverviewTab } from '~/components/admin/user-overview-tab'
import { Button } from '~/components/ui/button'
import { DATE_FORMAT_OPTIONS } from '~/components/ui/data-table'
import { EmptyState } from '~/components/ui/empty-state'
import { BackLink, PageHeader } from '~/components/ui/page-header'
import { SegmentedControl, SegmentedControlItem } from '~/components/ui/segmented-control'
import { Spinner } from '~/components/ui/spinner'
import { confirm } from '~/lib/confirm'
import { useStartImpersonation } from '~/lib/impersonation'
import { roleLabels } from '~/lib/members'
import { titleFor } from '~/lib/route-title'
import { api } from '~convex/_generated/api'
import type { Id } from '~convex/_generated/dataModel'

const TABS = ['overview', 'workspaces', 'activity', 'logins'] as const
type Tab = (typeof TABS)[number]

const parseTab = (value: unknown): Tab =>
  TABS.includes(value as Tab) ? (value as Tab) : 'overview'

export const Route = createFileRoute('/admin/users/$userId')({
  component: AdminUserDetail,
  head: () => ({ meta: [{ title: titleFor(msg`User`) }] }),
  validateSearch: (search): { tab?: Tab } =>
    search.tab === undefined ? {} : { tab: parseTab(search.tab) },
})

function AdminUserDetail() {
  const { t, i18n } = useLingui()
  const { userId } = Route.useParams()
  const { tab = 'overview' } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { data: me } = useQuery(convexQuery(api.users.currentUser, {}))
  const { data: user } = useQuery(convexQuery(api.admin.getUser, { userId: userId as Id<'users'> }))
  const { mutateAsync: setAdminFlag, isPending: isTogglingAdmin } = useMutation({
    mutationFn: useConvexMutation(api.admin.setAdminFlag),
  })
  const { mutateAsync: clearDeletionRequest, isPending: isReopening } = useMutation({
    mutationFn: useConvexMutation(api.admin.clearDeletionRequest),
  })
  const { start: startImpersonation, isPending: isStartingImpersonation } = useStartImpersonation()
  const isBusy = isTogglingAdmin || isStartingImpersonation || isReopening

  const labels: Record<Tab, string> = {
    activity: t`Activity`,
    logins: t`Logins`,
    overview: t`Overview`,
    workspaces: t`Workspaces`,
  }

  if (user === undefined) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (user === null) {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState
          description={t`It may have been deleted.`}
          icon={IconUsersOutline18}
          title={t`User not found`}
        />
      </div>
    )
  }

  const isSelf = me?._id === user._id

  const handleToggleAdmin = async () => {
    const granting = !user.admin
    const confirmed = await confirm({
      confirmLabel: granting ? t`Grant admin` : t`Revoke admin`,
      description: granting
        ? t`"${user.name}" will get full access to the cross-workspace admin surface.`
        : t`"${user.name}" will lose access to the admin surface.`,
      destructive: !granting,
      icon: <IconShieldKeyholeOutline18 />,
      title: granting ? t`Grant Planno admin?` : t`Revoke Planno admin?`,
    })
    if (!confirmed) {
      return
    }
    try {
      await setAdminFlag({ admin: granting, userId: user._id })
      toast.success(granting ? t`Admin access granted.` : t`Admin access revoked.`)
    } catch (error) {
      console.error('Toggle admin error:', error)
      toast.error(t`Couldn't update admin access.`)
    }
  }

  /*
   * The lever that makes closing an account survivable. Support's answer to
   * "I deleted it by mistake" — everything is still there, because this
   * phase flags rather than erases, and clearing the flag restores sign-in
   * and every live session on the next request.
   */
  const handleReopen = async () => {
    const confirmed = await confirm({
      confirmLabel: t`Reopen account`,
      description: t`"${user.name}" will be able to sign in again, with everything exactly as they left it.`,
      icon: <IconUndoOutline18 />,
      title: t`Reopen this account?`,
    })
    if (!confirmed) {
      return
    }
    try {
      const { reopened } = await clearDeletionRequest({ userId: user._id })
      // The mutation is idempotent, so a second admin getting there first
      // returns `false` rather than failing. Saying "reopened" then would
      // credit this click with something it did not do.
      toast.success(reopened ? t`Account reopened.` : t`This account was already open.`)
    } catch (error) {
      console.error('Clear deletion request error:', error)
      toast.error(t`Couldn't reopen the account.`)
    }
  }

  const handleImpersonate = async () => {
    const confirmed = await confirm({
      confirmLabel: t`Start session`,
      description: t`You'll use Planno as "${user.name}". Everything you do is attributed to them. The session runs until you exit it; there is no time limit.`,
      icon: <IconIncognitoOutline18 />,
      title: t`Use Planno as "${user.name}"?`,
    })
    if (!confirmed) {
      return
    }
    try {
      await startImpersonation(user._id)
    } catch (error) {
      console.error('Start impersonation error:', error)
      toast.error(t`Couldn't start the impersonation session.`)
    }
  }

  const workspaceRoleLabels = roleLabels(i18n)

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        above={<BackLink to="/admin/users">{t`Users`}</BackLink>}
        below={
          // The strip scrolls sideways rather than wrapping — four tabs in
          // pt-BR outrun a phone's width, and a wrapped tab row reads as two
          // separate controls.
          <div className="flex shrink-0 overflow-x-auto px-5 pb-3">
            <SegmentedControl
              onValueChange={(next) => navigate({ search: { tab: next } })}
              value={tab}
            >
              {TABS.map((value) => (
                <SegmentedControlItem key={value} value={value}>
                  {labels[value]}
                </SegmentedControlItem>
              ))}
            </SegmentedControl>
          </div>
        }
        meta={user.email ?? undefined}
        title={
          <span className="flex min-w-0 items-center gap-3">
            <AdminAvatar image={user.image} name={user.name} size="lg" />
            <span className="truncate">{user.name}</span>
            {user.admin ? (
              <span className="shrink-0">
                <DetailBadge tone="primary">
                  <Trans>Admin</Trans>
                </DetailBadge>
              </span>
            ) : null}
            {user.deletionRequestedAt === null ? null : (
              <span className="shrink-0">
                <DetailBadge tone="destructive">
                  <Trans>Closed</Trans>
                </DetailBadge>
              </span>
            )}
          </span>
        }
      >
        {user.deletionRequestedAt === null ? null : (
          <Button
            disabled={isBusy}
            onClick={handleReopen}
            size="sm"
            type="button"
            variant="outline"
          >
            <IconUndoOutline18 />
            <Trans>Reopen account</Trans>
          </Button>
        )}
        {/* Impersonating a staff account is refused server-side (privilege
            confusion), and so is impersonating yourself. */}
        {isSelf || user.admin ? null : (
          <Button
            disabled={isBusy}
            onClick={handleImpersonate}
            size="sm"
            type="button"
            variant="outline"
          >
            <IconIncognitoOutline18 />
            <Trans>Use as this account</Trans>
          </Button>
        )}
        <Button
          disabled={isBusy || (isSelf && user.admin)}
          onClick={handleToggleAdmin}
          size="sm"
          title={isSelf && user.admin ? t`You can't revoke your own admin access` : undefined}
          type="button"
          variant="outline"
        >
          <IconShieldKeyholeOutline18 />
          {user.admin ? <Trans>Revoke admin</Trans> : <Trans>Grant admin</Trans>}
        </Button>
      </PageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {tab === 'overview' ? <UserOverviewTab user={user} /> : null}
        {tab === 'workspaces' ? (
          <div className="px-5 py-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-border border-b text-left text-muted-foreground text-xs">
                  <th className="py-2 pr-3 font-medium">
                    <Trans>Workspace</Trans>
                  </th>
                  <th className="py-2 pr-3 font-medium">
                    <Trans>Role</Trans>
                  </th>
                  <th className="hidden py-2 font-medium sm:table-cell">
                    <Trans>Joined</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                {user.memberships.map((membership) => (
                  <tr
                    className="border-border/50 border-b last:border-0"
                    key={membership.workspaceId}
                  >
                    <td className="py-2 pr-3">
                      <Link
                        className="font-medium hover:underline"
                        params={{ workspaceId: membership.workspaceId }}
                        to="/admin/workspaces/$workspaceId"
                      >
                        {membership.name}
                      </Link>
                      <span className="ml-2 inline-flex gap-1.5">
                        {membership.isOwner ? (
                          <DetailBadge tone="primary">
                            <Trans>Owner</Trans>
                          </DetailBadge>
                        ) : null}
                        {membership.deletedAt === null ? null : (
                          <DetailBadge>
                            <Trans>Deleted</Trans>
                          </DetailBadge>
                        )}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {workspaceRoleLabels[membership.role]}
                    </td>
                    <td className="hidden py-2 text-muted-foreground sm:table-cell">
                      {i18n.date(membership.joinedAt, DATE_FORMAT_OPTIONS)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {tab === 'activity' ? (
          <div className="px-5 py-4">
            <ActivitySessionFeed scope={{ actorId: user._id, kind: 'actor' }} showActor={false} />
          </div>
        ) : null}
        {tab === 'logins' ? <LoginHistoryList userId={user._id} /> : null}
      </div>
    </div>
  )
}
